'use strict';

const Enum = require('./enum');

module.exports.SUBSCRIPTION_CALLBACK_LOCK_REDIS_KEY_PREFIX = 'SUBSCRIPTION:CALLBACK:LOCK:';
module.exports.SUBSCRIPTION_DVR_TRIAL_EXPIRATION_CALLBACK_REDIS_KEY_PREFIX = 'SUBSCRIPTION:TRIAL:EXPIRATION:';

/**
 * Defines a set of status a subscription can be in
 *
 * @enum
 * @readonly
 */
module.exports.SubscriptionStatus = Object.create(Enum, {
  CREATED: { value: 'created', enumerable: true }, // AAM when a house is set up, but has no bridge or doorbell yet
  TRIAL: { value: 'trial', enumerable: true },
  TRIAL_EXPIRED: { value: 'trial_expired', enumerable: true }, // not used yet
  PAID: { value: 'paid', enumerable: true },
  PAST_DUE: { value: 'past_due', enumerable: true }, // not used yet
  EXPIRED: { value: 'expired', enumerable: true },
  CANCELLED: { value: 'cancelled', enumerable: true },

  isActive: {
    value: function (status) {
      switch (status) {
        case this.TRIAL:
        case this.PAID:
        case this.CANCELLED:
          return true;
        default:
          return false;
      }
    },
    enumerable: false,
  },
});

/**
 * Subscription types
 *
 * @enum
 * @readonly
 */
module.exports.SubscriptionType = Object.create(Enum, {
  DOORBELL_DVR: { value: 'doorbellDvr', enumerable: true },
  LOCK_AAM: { value: 'lockAam', enumerable: true },
  HOUSE_AAM: { value: 'houseAam', enumerable: true },
  UNKNOWN: { value: 'unknown', enumerable: true },

  fromPlanCode: {
    value: function (planCode) {
      switch (planCode) {
        case 'ap_monthly':
        case 'ap_monthly_s':
        case 'ap_annual':
          return this.DOORBELL_DVR;

        case 'aam_lock_monthly':
        case 'aam_lock_annual':
          return this.LOCK_AAM;

        case 'aam_house_monthly':
        case 'aam_house_annual':
          return this.HOUSE_AAM;

        default:
          return this.UNKNOWN;
      }
    },
    enumerable: false,
  },
});

/**
 * Subscription plan descriptions
 *
 * @enum
 * @readonly
 */
module.exports.SubscriptionPlanDescription = Object.create(Enum, {
  TRIAL: { value: 'August Free Trial', enumerable: true },
  DOORBELL_DVR: { value: 'August Video Recording', enumerable: true },
  LOCK_AAM: { value: 'August Active Monitoring', enumerable: true },
  HOUSE_AAM: { value: 'August Active Monitoring', enumerable: true },
  UNKNOWN: { value: 'August', enumerable: true },

  fromPlanCode: {
    value: function (planCode) {
      switch (planCode) {
        case 'ap_monthly':
        case 'ap_monthly_s':
        case 'ap_annual':
          return this.DOORBELL_DVR;

        case 'aam_lock_monthly':
        case 'aam_lock_annual':
          return this.LOCK_AAM;

        case 'aam_house_monthly':
        case 'aam_house_annual':
          return this.HOUSE_AAM;

        case 'trial':
          return this.TRIAL;

        default:
          return this.UNKNOWN;
      }
    },
    enumerable: false,
  },

  fromSubscriptionType: {
    value: function (subscriptionType) {
      switch (subscriptionType) {
        case this.SubscriptionType.DOORBELL_DVR:
          return this.DOORBELL_DVR;

        case this.SubscriptionType.LOCK_AAM:
          return this.LOCK_AAM;

        case this.SubscriptionType.HOUSE_AAM:
          return this.HOUSE_AAM;

        default:
          return this.UNKNOWN;
      }
    },
    enumerable: false,
  },
});

/**
 * Subscription source systems
 *
 * @enum
 * @readonly
 */
module.exports.SubscriptionSource = Object.create(Enum, {
  RECURLY: { value: 'Recurly', enumerable: true },
});

/**
 * Trial Subscription related values
 *
 * @enum
 * @readonly
 */
module.exports.SubscriptionTrial = Object.create(Enum, {
  PLAN_CODE: { value: 'trial', enumerable: true },

  /**
   * @deprecated use the separate constants below instead
   */
  PERIOD_DAYS: { value: 30, enumerable: true },

  DVR_PERIOD_DAYS: { value: 30, enumerable: true },
  AAM_PERIOD_DAYS: { value: 180, enumerable: true },

  EXPIRATION_REMINDER_DAYS: { value: 7, enumerable: true },
  EXPIRATION_WARNING_DAYS: { value: 1, enumerable: true },
});

/**
 * SubscriptionPlanStatus
 *
 * @enum
 * @readonly
 */
module.exports.SubscriptionPlanStatus = Object.create(Enum, {
  ACTIVE: { value: 'active', enumerable: true },
  GRANDFATHERED: { value: 'grandfathered', enumerable: true },
  INACTIVE: { value: 'inactive', enumerable: true },
});

/**
 * Subscription plans including former plans
 *
 * NOTE prices will likely be removed when actually introducing multi currency in subscriptions-ui
 */
module.exports.SubscriptionPlans = {
  toArray: function () {
    return [this.trial, this.ap_annual, this.ap_monthly, this.ap_monthly_s];
  },

  trial: {
    planCode: 'trial',
    status: this.SubscriptionPlanStatus.INACTIVE,
    name: 'subscriptionNameTrial',
    description: 'subscriptionDescriptionTrial',
    planIntervalLength: 1,
    planIntervalUnit: 'months',
    videoAccessDays: 30,
    unitAmountInCents: {
      USD: 0,
      EUR: 0,
      GBP: 0,
      AUD: 0,
      CAD: 0,
    },
    setupFeeInCents: {
      USD: 0,
      EUR: 0,
      GBP: 0,
      AUD: 0,
      CAD: 0,
    },
    source: 'august',
  },

  ap_annual: {
    planCode: 'ap_annual',
    status: this.SubscriptionPlanStatus.GRANDFATHERED,
    name: 'subscriptionNameApAnnual',
    description: 'subscriptionDescriptionApAnnual',
    planIntervalLength: 12,
    planIntervalUnit: 'months',
    videoAccessDays: 30,
    unitAmountInCents: {
      USD: 4999,
      EUR: 4999,
      GBP: 4999,
      AUD: 6950,
      CAD: 6590,
    },
    setupFeeInCents: {
      USD: 0,
      EUR: 0,
      GBP: 0,
      AUD: 0,
      CAD: 0,
    },
    source: 'recurly',
  },

  ap_monthly: {
    planCode: 'ap_monthly',
    status: this.SubscriptionPlanStatus.ACTIVE,
    name: 'subscriptionNameApMonthly',
    description: 'subscriptionDescriptionApMonthly',
    planIntervalLength: 1,
    planIntervalUnit: 'months',
    videoAccessDays: 30,
    unitAmountInCents: {
      USD: 499,
      EUR: 499,
      GBP: 499,
      AUD: 695,
      CAD: 659,
    },
    setupFeeInCents: {
      USD: 0,
      EUR: 0,
      GBP: 0,
      AUD: 0,
      CAD: 0,
    },
    source: 'recurly',
  },

  ap_monthly_s: {
    planCode: 'ap_monthly_s',
    status: this.SubscriptionPlanStatus.ACTIVE,
    name: 'subscriptionNameApMonthlyS',
    description: 'subscriptionDescriptionApMonthlyS',
    planIntervalLength: 1,
    planIntervalUnit: 'months',
    videoAccessDays: 15,
    unitAmountInCents: {
      USD: 299,
      EUR: 299,
      GBP: 299,
      AUD: 425,
      CAD: 395,
    },
    setupFeeInCents: {
      USD: 0,
      EUR: 0,
      GBP: 0,
      AUD: 0,
      CAD: 0,
    },
    source: 'recurly',
  },
};
